'use client';

import React, { useRef, useState } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { AnimatePresence, motion } from 'framer-motion';

gsap.registerPlugin(ScrollTrigger);

const founders = [
    {
        name: "Abdul Sohail Samad",
        role: "Founder & Lead Developer",
        image: "/abdulsohailsamad.jpg",
        bio: "Expert Full-Stack Developer architecting robust, scalable digital infrastructures.",
        socials: { linkedin: "#", twitter: "#" }
    }
];

const MobileFoundersSection = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [selectedFounder, setSelectedFounder] = useState<typeof founders[0] | null>(null);

    useGSAP(() => {
        const cards = gsap.utils.toArray<HTMLElement>('.founder-card');

        cards.forEach((card, i) => {
            gsap.from(card, {
                scrollTrigger: {
                    trigger: card,
                    start: 'top 85%',
                    toggleActions: 'play none none reverse'
                },
                y: 50,
                opacity: 0,
                duration: 1,
                ease: 'power3.out'
            });
        });

    }, { scope: containerRef });

    return (
        <section ref={containerRef} className="py-20 bg-slate-50 overflow-hidden">
            <div className="px-6 mb-12 text-center">
                <span className="text-xs font-bold text-indigo-600 tracking-[0.2em] uppercase block mb-3">
                    The Visionaries
                </span>
                <h2 className="text-4xl font-black text-slate-900 tracking-tight leading-tight">
                    Meet the <br />
                    <span className="italic font-serif text-indigo-600">Architects</span>
                </h2>
            </div>

            <div className="flex flex-col gap-8 px-4">
                {founders.map((founder, idx) => (
                    <div
                        key={idx}
                        className="founder-card relative bg-white rounded-[2rem] p-6 shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden"
                    >
                        {/* Decorative Background Blur */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50 rounded-full blur-3xl -mr-10 -mt-10 opacity-60" />

                        <div className="relative z-10 flex flex-col items-center text-center">
                            {/* Avatar with Verified Tick */}
                            <div className="relative mb-6 cursor-pointer" onClick={() => setSelectedFounder(founder)}>
                                <motion.div
                                    layoutId={`avatar-${founder.name}`}
                                    className="w-32 h-32 rounded-full p-1 bg-gradient-to-br from-indigo-500 to-violet-500 shadow-lg"
                                >
                                    <div className="w-full h-full rounded-full border-4 border-white overflow-hidden relative">
                                        <Image
                                            src={founder.image}
                                            alt={founder.name}
                                            fill
                                            className="object-cover"
                                        />
                                    </div>
                                </motion.div>
                                {/* Verified Badge */}
                                <div className="absolute bottom-1 right-1 bg-blue-500 text-white p-1.5 rounded-full ring-4 ring-white shadow-sm pointer-events-none">
                                    <Icon name="CheckIcon" className="w-3 h-3" />
                                </div>
                            </div>

                            {/* Name & Role */}
                            <h3 className="text-2xl font-serif font-bold text-slate-900 mb-2">
                                {founder.name}
                            </h3>
                            <div className="inline-flex items-center space-x-2 px-3 py-1 bg-slate-50 rounded-full border border-slate-100 mb-5 max-w-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                                <span className="text-xs font-mono text-slate-600 uppercase tracking-tight truncate">
                                    {founder.role}
                                </span>
                            </div>

                            {/* Bio */}
                            <p className="text-slate-600 leading-relaxed text-sm mb-6 max-w-xs mx-auto">
                                "&nbsp;{founder.bio}&nbsp;"
                            </p>

                            {/* Action */}
                            <a
                                href="https://www.instagram.com/abdulsohailsamad"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full py-3 rounded-xl bg-slate-900 text-white font-bold text-sm tracking-wide hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 group"
                            >
                                <span>View Profile</span>
                                <Icon name="ArrowRightIcon" className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                            </a>
                        </div>
                    </div>
                ))}
            </div>

            {/* Lightbox Overlay */}
            <AnimatePresence>
                {selectedFounder && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSelectedFounder(null)}
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/90 backdrop-blur-md p-6"
                    >
                        <motion.div
                            layoutId={`avatar-${selectedFounder.name}`}
                            className="relative w-full max-w-sm aspect-[3/4] rounded-[2rem] overflow-hidden shadow-2xl bg-white border-4 border-white"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Image
                                src={selectedFounder.image}
                                alt={selectedFounder.name}
                                fill
                                className="object-cover"
                            />
                            {/* Close Button */}
                            <button
                                onClick={() => setSelectedFounder(null)}
                                className="absolute top-4 right-4 w-10 h-10 bg-black/50 text-white rounded-full flex items-center justify-center backdrop-blur-sm hover:bg-black/70 transition-colors"
                            >
                                <Icon name="XMarkIcon" className="w-5 h-5" />
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default MobileFoundersSection;
